import { Button, SpaceBetween, Spinner } from "@cloudscape-design/components";
import { useState } from "react";
import { RegistrationData } from "../../../data/FormData";
import { NewAccountPageTrigger, PagesEnum } from "./Utils/PageTrigger";
import { binaryOptions, phoneList } from "./Questions/questionOptions";
import SingleSelectComponent from "../../../components/SingleSelect";
import { TextInput } from "../../../components/TextInput";
import { ErrorPopup } from "../../../components/ErrorPopup";
import { validateInput } from "../../../utility-function/InputValidation";

export function QuestionnaireSimplePage(): JSX.Element {
    const [success, setSuccess] = useState<boolean>(false);
    const [submitWaiting, setSubmitWaiting] = useState<boolean>(false);
    const [submitFailed, setSubmitFailed] = useState<boolean>(false);
    const [errorPopupMessage, setErrorPopupMessage] = useState<string | undefined>(undefined);

    const [takeBloodPressureMedicationValue, setTakeBloodPressureMedicationValue] = useState<string>(RegistrationData.instance.bloodPressureMedication);
    const [showBPMedicationMessage, setShowBPMedicationMessage] = useState<boolean>(false);

    const [takeDiabetesMedicationValue, setTakeDiabetesMedicationValue] = useState<string>(RegistrationData.instance.dmMedication);
    const [showDMMedicationMessage, setShowDMMedicationMessage] = useState<boolean>(false);

    const [takeInsulinValue, setTakeInsulinValue] = useState<string>(RegistrationData.instance.insulin);
    const [showInsulinMessage, setShowInsulinMessage] = useState<boolean>(false);

    const [phoneModel, setPhoneModel] = useState<'Apple' | 'Android' | ''>(RegistrationData.instance.phoneType);
    const [showPhoneModelMessage, setShowPhoneModelMessage] = useState<boolean>(false);

    const [deviceType, setDeviceType] = useState<'Apple Watch' | 'Fitbit' | 'No' | 'opt-out' | ''>(RegistrationData.instance.deviceType);
    const [showDeviceTypeMessage, setShowDeviceTypeMessage] = useState<boolean>(false);

    const [address, setAddress] = useState<string>(RegistrationData.instance.address);
    const [showAddressMessage, setShowAddressMessage] = useState<boolean>(false);

    const [addressCity, setAddressCity] = useState<string>(RegistrationData.instance.addressCity);
    const [showAddressCityMessage, setShowAddressCityMessage] = useState<boolean>(false);

    const [addressState, setAddressState] = useState<string>(RegistrationData.instance.addressState);
    const [showAddressStateMessage, setShowAddressStateMessage] = useState<boolean>(false);

    const [addressZipcode, setAddressZipcode] = useState<string>(RegistrationData.instance.addressZipcode);
    const [showAddressZipcodeMessage, setShowAddressZipcodeMessage] = useState<boolean>(false);

    function gotoPage(page: PagesEnum) {
        if (NewAccountPageTrigger.instance.setNewAccountShowPage) {
            NewAccountPageTrigger.instance.setNewAccountShowPage(page)
        }
    }

    function submit() {
        const whenSuccess = (): void => {
            setSuccess(true)
        }
        const whenFail = (errorMessage?: string): void => {
            setSubmitWaiting(false)
            if (errorMessage) {
                setErrorPopupMessage(errorMessage)
            } else {
                setSubmitFailed(true)
            }
        }

        const validateField = (isValid: boolean, setMessage: (arg0: boolean) => void): boolean => {
            if (!isValid) {
                setMessage(true); // Show error message
                return false;
            }
            setMessage(false); // Hide error message
            return true;
        };

        const validateAllFields = (): boolean => {
            let validate = true;

            // Validate individual fields and update their corresponding message visibility
            if (RegistrationData.instance.show_htn_med_question) {
                validate = validate && validateField(validateInput(takeBloodPressureMedicationValue), setShowBPMedicationMessage);
            }
            if (RegistrationData.instance.show_dm_med_question) {
                validate = validate && validateField(validateInput(takeDiabetesMedicationValue), setShowDMMedicationMessage);
            }
            if (RegistrationData.instance.show_insulin_question) {
                validate = validate && validateField(validateInput(takeInsulinValue), setShowInsulinMessage);
            }
            validate = validate && validateField(validateInput(phoneModel), setShowPhoneModelMessage);
            if (RegistrationData.instance.show_wearable_device_question) {
                validate = validate && validateField(validateInput(deviceType), setShowDeviceTypeMessage);
            }
            if (RegistrationData.instance.deviceType === "No") {
                validate = validate && validateField(validateInput(address), setShowAddressMessage);
                validate = validate && validateField(validateInput(addressCity), setShowAddressCityMessage);
                validate = validate && validateField(validateInput(addressState), setShowAddressStateMessage);
                validate = validate && validateField(validateInput(addressZipcode), setShowAddressZipcodeMessage);
            }
            return validate;
        };

        // Example usage
        const isValid = validateAllFields();
        if (isValid) {
            setSubmitWaiting(true)
            RegistrationData.instance.submit(whenSuccess, whenFail)// Proceed with the next steps, e.g., form submission
        } else {
            // Handle validation errors
            return
        }
    }

    // Are you taking blood pressure medication?
    function renderBpMedicationQuestion(): JSX.Element | null {
        const htnMedicationQuestion: JSX.Element =
            <SingleSelectComponent
                question="Are you taking blood pressure medication?"
                options={binaryOptions}
                value={takeBloodPressureMedicationValue}
                onChange={(value) => {
                    setTakeBloodPressureMedicationValue(value);
                    RegistrationData.instance.bloodPressureMedication = value;
                }}
                showMessage={showBPMedicationMessage}
            />;
        if (RegistrationData.instance.show_htn_med_question) {
            return htnMedicationQuestion
        } else {
            return null
        }
    }

    // "Are you taking diabetes medication?"
    function renderDmMedicationQuestion(): JSX.Element | null {
        const dmMedicationQuestion: JSX.Element = <SingleSelectComponent
            question="Are you taking diabetes medication?"
            options={binaryOptions}
            value={takeDiabetesMedicationValue}
            onChange={(value) => {
                setTakeDiabetesMedicationValue(value)
                RegistrationData.instance.dmMedication = value
            }}
            showMessage={showDMMedicationMessage}
        />;
        if (RegistrationData.instance.show_dm_med_question) {
            return dmMedicationQuestion
        } else {
            return null;
        }
    }
    //"“Are you taking insulin?”"
    function renderInsulinQuestion(): JSX.Element | null {
        const insulinQuestion: JSX.Element = <SingleSelectComponent
            question="Are you taking insulin?"
            options={binaryOptions}
            value={takeInsulinValue}
            onChange={(value) => {
                setTakeInsulinValue(value)
                RegistrationData.instance.insulin = value
            }}
            showMessage={showInsulinMessage}
        />;
        if (RegistrationData.instance.show_insulin_question) {
            return insulinQuestion
        } else {
            return null;
        }
    }

    function renderPhoneTypeQuestion(): JSX.Element {
        const phoneTypeQuestion: JSX.Element = <SingleSelectComponent
            question="Do you have an Apple or Android phone?"
            options={phoneList}
            value={phoneModel}
            onChange={(value) => {
                setPhoneModel(value as 'Apple' | 'Android')
                RegistrationData.instance.phoneType = value as 'Apple' | 'Android'
            }}
            showMessage={showPhoneModelMessage}
        />
        return phoneTypeQuestion;
    }

    // "Do you have an Apple Watch or Fitbit that you will use for the program?"
    function renderDeviceTypeQuestion(): JSX.Element | null {
        const deviceTypeList = [
            {
                value: 'Apple Watch',
                label: 'Yes, I will use my Apple Watch',
                show: RegistrationData.instance.phoneType === "Apple"
            },
            { value: 'Fitbit', label: 'Yes, I will use my Fitbit', show: true },
            { value: 'No', label: 'No, please send me a Fitbit', show: RegistrationData.instance.provide_device_option },
            {
                value: 'opt-out',
                label: "No, I prefer not to wear a device and will self-report my exercise and sleep data",
                show: RegistrationData.instance.opt_out_wearable_option
            }]

        const deviceTypeQuestion: JSX.Element = <SingleSelectComponent
            question="Do you have an Apple Watch or Fitbit that you will use for the program?"
            options={deviceTypeList}
            value={deviceType}
            onChange={(value) => {
                setDeviceType(value as 'Apple Watch' | 'Fitbit' | 'No' | 'opt-out')
                RegistrationData.instance.deviceType = value as 'Apple Watch' | 'Fitbit' | 'No' | 'opt-out'
            }}
            showMessage={showDeviceTypeMessage}
        />;
        if (RegistrationData.instance.show_wearable_device_question) {
            return deviceTypeQuestion
        } else {
            return null
        }
    }


    // Please enter your mailing address so that we can ship you the device(s)
    function renderAddressQuestion(): JSX.Element | null {
        const addressQuestion: JSX.Element = <SpaceBetween size='xxxs' direction='vertical'>
            <SpaceBetween size='m' direction='horizontal'>
                <div>Please enter your mailing address so that we can ship you the device(s)</div>
            </SpaceBetween>
            <TextInput
                value={address}
                onChange={(value) => {
                    setAddress(value);
                    RegistrationData.instance.address = value;
                }}
                placeholder="Address"
                showMessage={showAddressMessage}
            />
            <TextInput
                value={addressCity}
                onChange={(value) => {
                    setAddressCity(value);
                    RegistrationData.instance.addressCity = value;
                }}
                placeholder="City"
                showMessage={showAddressCityMessage}
            />
            <TextInput
                value={addressState}
                onChange={(value) => {
                    setAddressState(value);
                    RegistrationData.instance.addressState = value;
                }}
                placeholder="State"
                showMessage={showAddressStateMessage}
            />
            <TextInput
                value={addressZipcode}
                onChange={(value) => {
                    setAddressZipcode(value);
                    RegistrationData.instance.addressZipcode = value;
                }}
                placeholder="Zipcode"
                type="number"
                showMessage={showAddressZipcodeMessage}
            />
        </SpaceBetween>
        if (RegistrationData.instance.deviceType === "No") {
            return addressQuestion
        } else {
            return null;
        }

    }

    return <div>
        {errorPopupMessage && (
            <ErrorPopup
                message={errorPopupMessage}
                onClose={() => setErrorPopupMessage(undefined)}
            />
        )}
        {
            success &&
            <div>
                <SpaceBetween size='xl' direction='vertical'>
                    <div className='page-container'>
                        <div>You have successfully set up your account.</div>
                        <div>Please tap the button to install the app and login.</div>

                        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                            {phoneModel === 'Apple' ? (
                                <button
                                    onClick={() => window.location.href = 'https://apps.apple.com/ca/app/cipra-ai/id1567470393'}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        fontSize: '1rem',
                                        backgroundColor: '#007bff',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Install on iPhone
                                </button>
                            ) : phoneModel === 'Android' ? (
                                <button
                                    onClick={() => window.location.href = 'https://play.google.com/store/apps/details?id=ai.cipra.bpguidance'}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        fontSize: '1rem',
                                        backgroundColor: '#34a853',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Install on Android
                                </button>
                            ) : null}
                        </div>
                    </div>
                </SpaceBetween>
            </div>
        }
        {!success && <div>
            <SpaceBetween size='xl' direction='vertical'>
                {renderBpMedicationQuestion()}
                {renderDmMedicationQuestion()}
                {renderInsulinQuestion()}
                {renderPhoneTypeQuestion()}
                {renderDeviceTypeQuestion()}
                {renderAddressQuestion()}

                <div className='line-align-center'>
                    <Button className='line-items-padding' onClick={() => {
                        gotoPage(PagesEnum.First)
                    }} disabled={submitWaiting}>Back</Button>
                    <Button variant='primary' className='line-items-padding' onClick={submit} disabled={submitWaiting}>
                        {
                            submitWaiting && <Spinner />
                        }
                        Submit
                    </Button>
                </div>

                {
                    submitFailed && <p className='tip-text'>Submission Failed.</p>
                }
            </SpaceBetween>
        </div>
        }
    </div>
}
